<template>
  <div class="flex items-center">
    <button
      id="dropdown-whatsapp-button"
      data-dropdown-toggle="dropdown-whatsapp"
      class="flex-shrink-0 z-10 inline-flex items-center py-2.5 px-4 text-sm font-medium text-center
             text-gray-900 bg-gray-100 border border-gray-300 rounded-s-lg
             hover:bg-gray-200 focus:ring-4 focus:outline-none focus:ring-gray-100
             dark:bg-gray-700 dark:hover:bg-gray-600 dark:focus:ring-gray-700
             dark:text-white dark:border-gray-600"
      type="button"
    >
      <span class="font-mono">{{ currentCountry.code }}</span>
      <span class="mx-2">+{{ currentCountry.calling_code }}</span>
      <svg
        class="w-2.5 h-2.5"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 10 6"
      >
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m1 1 4 4 4-4"
        />
      </svg>
    </button>

    <div
      id="dropdown-whatsapp"
      class="z-10 hidden bg-white divide-y divide-gray-100 rounded-lg shadow w-72 max-h-96 overflow-y-auto dark:bg-gray-700 dark:divide-gray-700"
    >
      <ul
        class="py-2 text-sm text-gray-700 dark:text-gray-200"
        aria-labelledby="dropdown-whatsapp-button"
      >
        <li
          v-for="country in countries"
          :key="country.code"
        >
          <button
            type="button"
            class="inline-flex w-full px-4 py-2 text-sm
                   text-gray-700 hover:bg-gray-100
                   dark:text-gray-200 dark:hover:bg-gray-600
                   dark:hover:text-white"
            @click="onSelectCountry(country)"
          >
            <span class="inline-flex items-center w-full">
              <span class="font-mono mr-2">{{ country.code }}</span>
              <span class="flex-1 text-left">{{ country.name }}</span>
              <span class="text-gray-500 ml-2 dark:text-gray-400">+{{ country.calling_code }}</span>
            </span>
          </button>
        </li>
      </ul>
    </div>

    <div class="relative w-full">
      <input
        id="whatsapp-input"
        type="text"
        class="block p-2.5 w-full z-20 text-sm text-gray-900
               bg-gray-50 rounded-e-lg border-s-0 border border-gray-300
               focus:ring-blue-500 focus:border-blue-500
               dark:bg-gray-700 dark:border-s-gray-700 dark:border-gray-600
               dark:placeholder-gray-400 dark:text-white dark:focus:border-blue-500"
        :value="phoneNumber"
        @input="onNumberInput"
        :placeholder="$t('labels.whatsapp_number_placeholder')"
        pattern="[0-9]*"
        inputmode="numeric"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, watch, onMounted } from 'vue'
import { Dropdown } from 'flowbite';
import { useI18n } from 'vue-i18n';

const { locale } = useI18n();

interface Country {
  id: number;
  code: string;
  name: string;
  calling_code: string;
}

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  defaultCountryCode: {
    type: String,
    default: 'US',
  },
})

const emits = defineEmits(['update:modelValue'])

const phoneNumber = ref('')
const countries = ref<Country[]>([])
const selectedCountry = ref<Country | null>(null)

const currentCountry = computed(() => selectedCountry.value || {
  code: 'US',
  name: 'United States',
  calling_code: '1'
})

const combinedValue = computed({
  get() {
    // Only return combined value if there's an actual phone number
    if (!phoneNumber.value || phoneNumber.value.length === 0) {
      return ''
    }
    return '+' + currentCountry.value.calling_code + phoneNumber.value
  },
  set(newVal) {
    // Parse incoming value if needed
  },
})

watch([selectedCountry, phoneNumber], () => {
  emits('update:modelValue', combinedValue.value)
})

watch(
  () => props.modelValue,
  (newVal) => {
    if (!newVal) {
      phoneNumber.value = '';
      return;
    }
    let filtered = newVal.replace('+', '');
    const found = countries.value.find((c) => filtered.startsWith(c.calling_code))
    if (found) {
      selectedCountry.value = found
      phoneNumber.value = filtered.replace(found.calling_code, '')
    }
  },
  { immediate: true }
)

const onSelectCountry = (country: Country) => {
  selectedCountry.value = country
}

const onNumberInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  // Only allow numbers
  const cleaned = target.value.replace(/\D/g, '')
  phoneNumber.value = cleaned
  target.value = cleaned
}

const loadCountries = async () => {
  try {
    const lang = locale.value || 'en'
    const response = await fetch(`/countries/${lang}.json`)
    const data = await response.json()
    countries.value = data.data.sort((a: Country, b: Country) => a.name.localeCompare(b.name))
    
    // Set default country
    const defaultCountry = countries.value.find(c => c.code === props.defaultCountryCode.toUpperCase())
    if (defaultCountry) {
      selectedCountry.value = defaultCountry
    }
  } catch (error) {
    console.error('Failed to load countries:', error)
    // Fallback to a basic list if loading fails
    countries.value = [
      { id: 840, code: 'US', name: 'United States', calling_code: '1' },
      { id: 76, code: 'BR', name: 'Brazil', calling_code: '55' },
    ]
  }
}

onMounted(async () => {
  await loadCountries()
  
  // Initialize Flowbite dropdown
  const $targetEl = document.getElementById('dropdown-whatsapp');
  const $triggerEl = document.getElementById('dropdown-whatsapp-button');
  if ($targetEl && $triggerEl) {
    new Dropdown($targetEl, $triggerEl);
  }
})
</script>